/**
 * Word-level diff (LCS) for inline, color-coded comparison of prompt versions.
 *
 * `diffWords` returns an ordered list of ops: equal / del / add. Because
 * whitespace is tokenised as its own token, the ops can be concatenated back
 * together losslessly and rendered inline as a readable paragraph.
 */

import { escapeHtml } from './util';

export type DiffOpType = 'equal' | 'del' | 'add';

export interface DiffOp {
  type: DiffOpType;
  text: string;
}

export interface DiffStats {
  added: number;
  removed: number;
  unchanged: number;
  /** 0–100 similarity between the two versions. */
  similarity: number;
}

/** Safety valve: fall back to a coarse diff beyond this many DP cells. */
const MAX_DP_CELLS = 6_000_000;

/** Split into words *and* whitespace runs so the diff round-trips exactly. */
export function tokenize(text: string): string[] {
  if (!text) return [];
  return text.match(/\s+|[^\s]+/g) ?? [];
}

/** Classic LCS backtracking over token arrays. */
function lcsOps(a: string[], b: string[]): DiffOp[] {
  const n = a.length;
  const m = b.length;
  const width = m + 1;
  const dp = new Uint32Array((n + 1) * width);

  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) {
        dp[i * width + j] = dp[(i + 1) * width + (j + 1)] + 1;
      } else {
        dp[i * width + j] = Math.max(dp[(i + 1) * width + j], dp[i * width + (j + 1)]);
      }
    }
  }

  const ops: DiffOp[] = [];
  const push = (type: DiffOpType, text: string) => {
    const last = ops[ops.length - 1];
    if (last && last.type === type) last.text += text;
    else ops.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push('equal', a[i]);
      i++;
      j++;
    } else if (dp[(i + 1) * width + j] >= dp[i * width + (j + 1)]) {
      push('del', a[i]);
      i++;
    } else {
      push('add', b[j]);
      j++;
    }
  }
  while (i < n) push('del', a[i++]);
  while (j < m) push('add', b[j++]);

  return ops;
}

/** Coarse whole-content fallback for gigantic inputs (rare for prompts). */
function coarseOps(a: string, b: string): DiffOp[] {
  const ops: DiffOp[] = [];
  if (a === b) return [{ type: 'equal', text: a }];
  if (a) ops.push({ type: 'del', text: a });
  if (b) ops.push({ type: 'add', text: b });
  return ops;
}

/**
 * Word-level diff between two prompt versions.
 * Ordering of ops follows `b` (the newer version) for insertions.
 */
export function diffWords(before: string, after: string): DiffOp[] {
  if (before === after) {
    return before ? [{ type: 'equal', text: before }] : [];
  }
  const a = tokenize(before);
  const b = tokenize(after);
  if (a.length * b.length > MAX_DP_CELLS) {
    return coarseOps(before, after);
  }
  return lcsOps(a, b);
}

export function diffStats(ops: DiffOp[]): DiffStats {
  let added = 0;
  let removed = 0;
  let unchanged = 0;
  for (const op of ops) {
    const words = op.text.split(/\s+/).filter(Boolean).length;
    if (op.type === 'add') added += words;
    else if (op.type === 'del') removed += words;
    else unchanged += words;
  }
  const base = unchanged + removed;
  const similarity = base + added === 0 ? 100 : Math.round((unchanged / (base + added)) * 100);
  return { added, removed, unchanged, similarity };
}

/**
 * Render the diff as inline HTML: removed words struck through in red,
 * added words highlighted in green, untouched text plain.
 */
export function renderDiffHtml(ops: DiffOp[]): string {
  return ops
    .map((op) => {
      const text = escapeHtml(op.text);
      if (op.type === 'add') return `<span class="diff-add">${text}</span>`;
      if (op.type === 'del') return `<span class="diff-del">${text}</span>`;
      return text;
    })
    .join('');
}

/** Plain-text view (used for copy/export of a diff): `-old +new` markers. */
export function renderDiffText(ops: DiffOp[]): string {
  return ops
    .map((op) => {
      if (op.type === 'add') return `+${op.text}`;
      if (op.type === 'del') return `-${op.text}`;
      return op.text;
    })
    .join('');
}

/**
 * Split a diff into per-paragraph hunks so long prompts stay scannable.
 * Each hunk keeps its ops plus the stats for that region.
 */
export interface DiffHunk {
  ops: DiffOp[];
  stats: DiffStats;
}

export function diffHunks(ops: DiffOp[], blankLineGap = 2): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let current: DiffOp[] = [];

  const flush = () => {
    if (current.length) {
      hunks.push({ ops: current, stats: diffStats(current) });
      current = [];
    }
  };

  for (const op of ops) {
    if (op.type !== 'equal') {
      current.push(op);
      continue;
    }
    // Split untouched text on blank lines so each hunk is one paragraph.
    const parts = op.text.split(/(\n{2,})/g);
    for (const part of parts) {
      if (!part) continue;
      current.push({ type: 'equal', text: part });
      const newlines = (part.match(/\n/g) ?? []).length;
      if (/^\n+$/.test(part) && newlines >= blankLineGap) flush();
    }
  }
  flush();
  return hunks.length ? hunks : [{ ops, stats: diffStats(ops) }];
}
